import type { ReactNode } from 'react';
import { cn } from '@shared/utils';
import { Button } from '../button';

/*
 * 載入與錯誤兩種畫面用同一個容器高度與置中方式，從載入換成錯誤時內容不會跳位。
 * 預設高度留給 app 的頂端橫幅與分頁列；要撐滿整個視窗時傳 `className="min-h-dvh"`。
 */
const STATUS_SCREEN_CLASS =
  'flex min-h-[60dvh] flex-col items-center justify-center';

export interface LoadingScreenProps {
  /** app 的 logo，會被轉成灰階並脈動 */
  logo: ReactNode;
  label?: string;
  className?: string;
}

/**
 * 整頁的載入畫面。
 *
 * 「確認登入狀態」與「載入第一個頁面」是接連發生的兩段等待，兩段都用這個畫面，
 * 使用者就只會看到一次等待，中間不會先閃一個東西再換成另一個。
 *
 * Logo 轉灰階並壓到半透明，因為這一格是背景：保留彩色會讓它看起來像已經載完的畫面。
 * 脈動只加在 logo 上，文字跟著閃會難讀。
 */
export function LoadingScreen({
  logo,
  label = '載入中…',
  className,
}: LoadingScreenProps) {
  return (
    <div
      className={cn(STATUS_SCREEN_CLASS, 'gap-4', className)}
      role="status"
      aria-label={label}
    >
      <span className="animate-pulse opacity-40 grayscale">{logo}</span>
      <p className="font-mono text-xs tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}

export interface ErrorScreenProps {
  title: string;
  message: string;
  /** 有值時顯示「重試」鍵 */
  onRetry?: () => void;
  className?: string;
}

/**
 * 整頁的錯誤畫面，用在讀取失敗、手上沒有任何資料可顯示的時候。
 * 已有資料時改用頂部提示告知失敗，保留原本的畫面。
 */
export function ErrorScreen({
  title,
  message,
  onRetry,
  className,
}: ErrorScreenProps) {
  return (
    <div
      role="alert"
      className={cn(STATUS_SCREEN_CLASS, 'gap-3 px-6 text-center', className)}
    >
      <p className="font-display text-base text-strong">{title}</p>
      <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
        {message}
      </p>
      {onRetry && (
        <Button onClick={onRetry} className="mt-2 px-6">
          重試
        </Button>
      )}
    </div>
  );
}
